<template>
  <div class="scroller-container relative">
    <button v-if="isDesktop && showPrevArrow" type="button" class="nav-scroll-arrow prev" aria-label="Previous" @click="scroll('prev')">
      <i class="chevron-small-left-light-medium"></i>
    </button>

    <div ref="scrollerEl" class="nav-scroller" @scroll="updateArrows">
      <slot></slot>
    </div>

    <button v-if="isDesktop && showNextArrow" type="button" class="nav-scroll-arrow next" aria-label="Next" @click="scroll('next')">
      <i class="chevron-small-right-light-medium"></i>
    </button>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useEventListener, useThrottleFn } from '@vueuse/core';
import { useNavViewType } from '~/stores/navViewType';
import { GET_IS_DESKTOP_VIEW } from '#shared/types/getters';

const props = defineProps({
  updateArrowsAuto: {
    type: Boolean,
    default: false,
  },
});

const navViewType = useNavViewType();
const isDesktop = computed(() => navViewType[GET_IS_DESKTOP_VIEW]);

const scrollerEl = ref(null);
const showPrevArrow = ref(false);
const showNextArrow = ref(false);

const updateArrows = () => {
  if (!scrollerEl.value || !isDesktop.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = scrollerEl.value;
  showPrevArrow.value = scrollLeft > 0;
  // Use a small buffer (1px) for floating point precision issues
  showNextArrow.value = scrollLeft + clientWidth < scrollWidth - 1;
};

const scroll = (direction) => {
  if (!scrollerEl.value) return;

  const container = scrollerEl.value;
  const items = Array.from(container.querySelectorAll('li'));

  if (!items.length) return;

  const ARROW_WIDTH = 32;
  const HALF_PADDING = 8;
  const ARROW_OFFSET = ARROW_WIDTH + HALF_PADDING;
  const { scrollLeft: currentScroll, clientWidth, scrollWidth = 0 } = container;
  const containerRect = container.getBoundingClientRect();

  let targetScroll = currentScroll;
  const maxScroll = scrollWidth - clientWidth;

  if (direction === 'next') {
    const rightBoundary = containerRect.right - (showNextArrow.value ? ARROW_WIDTH : 0);
    const cutOffItem = items.find((item) => {
      const itemRect = item.getBoundingClientRect();
      return itemRect.right - 1 > rightBoundary;
    });

    if (cutOffItem) {
      const itemRect = cutOffItem.getBoundingClientRect();
      const distanceToScroll = itemRect.left - containerRect.left - ARROW_OFFSET;
      targetScroll = currentScroll + distanceToScroll;

      if (targetScroll <= currentScroll + 1) {
        targetScroll = currentScroll + clientWidth - ARROW_WIDTH * 2;
      }
    } else {
      targetScroll = currentScroll + clientWidth;
    }

    if (maxScroll - targetScroll < ARROW_OFFSET) {
      targetScroll = maxScroll;
    }
  } else if (direction === 'prev') {
    const leftBoundary = containerRect.left + (showPrevArrow.value ? ARROW_WIDTH : 0);
    const cutOffItem = [...items].reverse().find((item) => {
      const itemRect = item.getBoundingClientRect();
      return itemRect.left + 1 < leftBoundary;
    });

    if (cutOffItem) {
      const itemRect = cutOffItem.getBoundingClientRect();
      const distanceToScroll = containerRect.right - ARROW_OFFSET - itemRect.right;
      targetScroll = currentScroll - distanceToScroll;

      if (targetScroll >= currentScroll - 1) {
        targetScroll = currentScroll - clientWidth + ARROW_WIDTH * 2;
      }
    } else {
      targetScroll = currentScroll - clientWidth;
    }

    if (targetScroll < ARROW_OFFSET) {
      targetScroll = 0;
    }
  }

  container.scrollTo({
    left: targetScroll,
    behavior: 'smooth',
  });
};

let observer = null;

onMounted(() => {
  nextTick(() => updateArrows());

  if (import.meta.client && scrollerEl.value && props.updateArrowsAuto) {
    observer = new MutationObserver(() => {
      updateArrows();
    });
    observer.observe(scrollerEl.value, { childList: true, subtree: true, characterData: true });
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

const throttledUpdateArrows = useThrottleFn(updateArrows, 200);

if (import.meta.client) {
  useEventListener(window, 'resize', throttledUpdateArrows);
}

const resetScroll = () => {
  if (scrollerEl.value) {
    scrollerEl.value.scrollLeft = 0;
    updateArrows();
  }
};

// Expose methods to parent components
defineExpose({
  updateArrows,
  scroll,
  resetScroll,
});
</script>

<style lang="scss">
@import '~/assets/scss/scroller.scss';
@import '~/assets/scss/mixins/index.scss';

.gap-16 {
  gap: 16px;
}

.nav-scroll-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: $overlay-color-2;
  cursor: pointer;

  &::after {
    display: block;
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
  }

  &.prev {
    left: 0;
  }

  &.next {
    right: 0;
  }
}
</style>
